
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SocialGroupType, formSchema } from "@/types/social-groups";

interface EditGroupDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  group: SocialGroupType | null;
  isRTL: boolean;
}

export const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  group,
  isRTL
}) => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name_ar: group?.name.ar || "",
      name_en: group?.name.en || "",
      description_ar: group?.description.ar || "",
      description_en: group?.description.en || "",
      type: group?.type || "whatsapp",
      link: group?.link || "",
      members: group?.members || 0,
    },
  });

  // Update form values when group changes
  React.useEffect(() => {
    if (group) {
      form.reset({
        name_ar: group.name.ar,
        name_en: group.name.en,
        description_ar: group.description.ar,
        description_en: group.description.en,
        type: group.type,
        link: group.link,
        members: group.members,
      });
    }
  }, [group, form]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {isRTL ? "تعديل المجموعة" : "Edit Group"}
          </DialogTitle>
          <DialogDescription>
            {isRTL
              ? "قم بتعديل تفاصيل مجموعة التواصل الاجتماعي."
              : "Update social media group details."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الاسم (عربي)" : "Name (Arabic)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "اسم المجموعة بالعربية" : "Group name in Arabic"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الاسم (إنجليزي)" : "Name (English)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "اسم المجموعة بالإنجليزية" : "Group name in English"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الوصف (عربي)" : "Description (Arabic)"}</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder={isRTL ? "وصف المجموعة بالعربية" : "Group description in Arabic"} 
                        className="min-h-[80px] resize-none"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الوصف (إنجليزي)" : "Description (English)"}</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder={isRTL ? "وصف المجموعة بالإنجليزية" : "Group description in English"} 
                        className="min-h-[80px] resize-none"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "نوع المجموعة" : "Group Type"}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={isRTL ? "اختر نوع المجموعة" : "Select group type"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="whatsapp">
                          <div className="flex items-center gap-2">
                            <MessageSquare size={16} className="text-green-500" />
                            <span>{isRTL ? "واتساب" : "WhatsApp"}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="telegram">
                          <div className="flex items-center gap-2">
                            <Send size={16} className="text-blue-500" />
                            <span>{isRTL ? "تلجرام" : "Telegram"}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="members"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "عدد الأعضاء" : "Members Count"}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isRTL ? "رابط المجموعة" : "Group Link"}</FormLabel>
                  <FormControl>
                    <Input placeholder={isRTL ? "أدخل رابط المجموعة" : "Enter group link"} {...field} />
                  </FormControl>
                  <FormDescription>
                    {isRTL
                      ? "يجب أن يكون رابط صالح لمجموعة واتساب أو تلجرام"
                      : "Must be a valid WhatsApp or Telegram group link"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">{isRTL ? "حفظ التغييرات" : "Save Changes"}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
